import { useState } from 'react';
import {
  Badge,
  Button,
  ButtonIcon,
  Flex,
  SearchField,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import { RiAddLine, RiDeleteBinLine, RiEditLine } from '@remixicon/react';
import {
  TrackedSubscription,
  TrackedTopic,
  ServiceBusSubscription,
  ItemStatus,
} from '../types';
import { SubscriptionDialog } from './SubscriptionDialog';
import styles from '../ServiceBusTopicManager.module.css';

interface SubscriptionListProps {
  subscriptions: TrackedSubscription[];
  topics: TrackedTopic[];
  selectedTopic: string | null;
  onAdd: (subscription: ServiceBusSubscription) => void;
  onEdit: (id: string, subscription: ServiceBusSubscription) => void;
  onDelete: (id: string) => void;
  onRestore: (id: string) => void;
}

const statusLabel: Record<ItemStatus, string> = {
  original: '',
  added: 'novo',
  modified: 'editado',
  deleted: 'removido',
};

export const SubscriptionList = ({
  subscriptions,
  topics,
  selectedTopic,
  onAdd,
  onEdit,
  onDelete,
  onRestore,
}: SubscriptionListProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSub, setEditingSub] = useState<TrackedSubscription | null>(
    null,
  );
  const [filter, setFilter] = useState('');

  const activeTopics = topics.filter(t => t.status !== 'deleted');
  const filteredSubs = selectedTopic
    ? subscriptions.filter(s => s.topic === selectedTopic)
    : subscriptions;
  const visibleSubs = filter
    ? filteredSubs.filter(s =>
        s.name.toLowerCase().includes(filter.toLowerCase()),
      )
    : filteredSubs;
  const activeSubs = filteredSubs.filter(s => s.status !== 'deleted');
  const existingSubscriptions = subscriptions
    .filter(s => s.status !== 'deleted')
    .map(s => ({ name: s.name, topic: s.topic, id: s.id }));

  const handleOpenCreate = () => {
    setEditingSub(null);
    setDialogOpen(true);
  };

  return (
    <Flex direction="column">
      <Flex align="center" justify="between">
        <Text variant="title-small">
          Subscriptions
          {selectedTopic ? ` — ${selectedTopic}` : ''} ({activeSubs.length})
        </Text>
        <Button
          variant="secondary"
          onClick={handleOpenCreate}
          isDisabled={activeTopics.length === 0}
        >
          Nova Subscription
        </Button>
      </Flex>

      {!selectedTopic && (
        <div className={styles.emptyState}>
          <Text variant="body-small">
            Selecione um tópico à esquerda para ver suas subscriptions.
          </Text>
        </div>
      )}

      {selectedTopic && filteredSubs.length > 0 && (
        <SearchField
          placeholder="Buscar subscription..."
          value={filter}
          onChange={setFilter}
        />
      )}

      {selectedTopic && filteredSubs.length === 0 && (
        <div className={styles.emptyState}>
          <Text variant="body-small">
            Nenhuma subscription para o tópico "{selectedTopic}". Clique em
            "Nova Subscription" para adicionar.
          </Text>
        </div>
      )}

      {selectedTopic &&
        visibleSubs.length === 0 &&
        filteredSubs.length > 0 &&
        filter && (
          <div className={styles.emptyState}>
            <Text variant="body-small">
              Nenhuma subscription encontrada para "{filter}".
            </Text>
          </div>
        )}

      {selectedTopic && visibleSubs.length > 0 && (
        <div className={styles.list}>
          {visibleSubs.map(sub => (
            <div
              key={sub.id}
              className={`${styles.listItem} ${
                sub.status === 'deleted' ? styles.deletedItem : ''
              }`}
            >
              <Flex direction="column">
                <Flex align="center">
                  <Text variant="body-small">{sub.name}</Text>
                  {sub.status !== 'original' && (
                    <Badge size="small">{statusLabel[sub.status]}</Badge>
                  )}
                </Flex>
                <Text className={styles.sqlFilter}>{sub.sql_filter}</Text>
                <Flex>
                  {sub.lock_duration && (
                    <Badge size="small">lock: {sub.lock_duration}</Badge>
                  )}
                  {sub.max_delivery_count !== undefined && (
                    <Badge size="small">
                      max delivery: {sub.max_delivery_count}
                    </Badge>
                  )}
                </Flex>
              </Flex>
              <Flex>
                {sub.status === 'deleted' ? (
                  <TooltipTrigger>
                    <ButtonIcon
                      aria-label="Restaurar"
                      variant="secondary"
                      icon={<RiAddLine size={16} />}
                      onPress={() => onRestore(sub.id)}
                    />
                    <Tooltip>Restaurar</Tooltip>
                  </TooltipTrigger>
                ) : (
                  <>
                    <TooltipTrigger>
                      <ButtonIcon
                        aria-label="Editar"
                        variant="secondary"
                        icon={<RiEditLine size={16} />}
                        onPress={() => {
                          setEditingSub(sub);
                          setDialogOpen(true);
                        }}
                      />
                      <Tooltip>Editar</Tooltip>
                    </TooltipTrigger>
                    <TooltipTrigger>
                      <ButtonIcon
                        aria-label="Deletar"
                        variant="secondary"
                        icon={<RiDeleteBinLine size={16} />}
                        onPress={() => onDelete(sub.id)}
                      />
                      <Tooltip>Deletar</Tooltip>
                    </TooltipTrigger>
                  </>
                )}
              </Flex>
            </div>
          ))}
        </div>
      )}

      <SubscriptionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={subscription => {
          if (editingSub) {
            onEdit(editingSub.id, subscription);
          } else {
            onAdd(subscription);
          }
        }}
        initialData={editingSub || undefined}
        topics={activeTopics}
        existingSubscriptions={existingSubscriptions}
        editingId={editingSub?.id}
        mode={editingSub ? 'edit' : 'create'}
        preselectedTopic={selectedTopic || undefined}
      />
    </Flex>
  );
};
